<template>
  <section class="pay-range-identity-summary" aria-labelledby="pay-range-identity-summary-title">
    <div class="pay-range-identity-summary__header">
      <h3 id="pay-range-identity-summary-title" class="pay-range-identity-summary__title">
        Pay range identity
      </h3>
      <span class="pay-range-identity-summary__hint">{{ hint }}</span>
    </div>
    <dl class="pay-range-identity-summary__fields">
      <div class="pay-range-identity-summary__field">
        <dt class="pay-range-identity-summary__label">Job Family Level</dt>
        <dd class="pay-range-identity-summary__value">{{ jobFamilyLabel }}</dd>
      </div>
      <div class="pay-range-identity-summary__field">
        <dt class="pay-range-identity-summary__label">Location</dt>
        <dd class="pay-range-identity-summary__value">{{ locationLabel }}</dd>
      </div>
      <div class="pay-range-identity-summary__field">
        <dt class="pay-range-identity-summary__label">Currency</dt>
        <dd class="pay-range-identity-summary__value">{{ currencyLabel }}</dd>
      </div>
      <div class="pay-range-identity-summary__field">
        <dt class="pay-range-identity-summary__label">Pay Basis</dt>
        <dd class="pay-range-identity-summary__value">{{ payBasisLabel }}</dd>
      </div>
      <div class="pay-range-identity-summary__field">
        <dt class="pay-range-identity-summary__label">Work mode</dt>
        <dd class="pay-range-identity-summary__value">{{ workModeLabel }}</dd>
      </div>
    </dl>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { formatLevelLabel } from "@components/JobFamilyPickerDialog/jobFamilyPickerDialogUtils";

/**
 * Step 2 (Amounts) read-only identity summary (ER-12153 / ER-12157, Figma 4352:64541).
 * Identity is editable only in Step 1; Step 2 never allows identity edits.
 * The five string props, when set, are shown verbatim and win over the `identity` object.
 */
const props = defineProps({
  hint: {
    type: String,
    default: "Set in Step 1 · go Back to change",
  },
  jobFamilyLevel: {
    type: String,
    default: "",
  },
  location: {
    type: String,
    default: "",
  },
  currency: {
    type: String,
    default: "",
  },
  payBasis: {
    type: String,
    default: "",
  },
  workMode: {
    type: String,
    default: "",
  },
  identity: {
    type: Object,
    default: () => ({}),
  },
  payBasisOptions: {
    type: Array,
    default: () => [],
  },
  workModeOptions: {
    type: Array,
    default: () => [],
  },
});

const jobFamilyLabel = computed(() => {
  if (props.jobFamilyLevel) return props.jobFamilyLevel;
  const jf = props.identity.jobFamily || {};
  if (jf.jobFamilyName) {
    const path = jf.level ? `${jf.jobFamilyName} › ${formatLevelLabel(jf.level)}` : jf.jobFamilyName;
    const tag = String(jf.levelId ?? jf.code ?? "").replace(/^#/, "");
    return tag ? `${path} #${tag}` : path;
  }
  return props.identity.jobFamilyLevel?.name || "—";
});

const locationLabel = computed(() => {
  if (props.location) return props.location;
  const loc = props.identity.location || {};
  const parts = [];
  if (loc.countryName) parts.push(loc.countryName);
  if (loc.regionName) parts.push(loc.regionName);
  if (loc.subRegionName) parts.push(loc.subRegionName);
  if (parts.length === 0) return "Country Wide";
  if (parts.length === 1 && loc.scope === "country-wide") return `${parts[0]} · Country Wide`;
  return parts.join(" · ");
});

const currencyLabel = computed(() => {
  if (props.currency) return props.currency;
  const c = props.identity.operatingCurrency || {};
  const name = c.name || "";
  const code = c.code || "";
  if (name && code) return `${name} (${code})`;
  return name || code || "—";
});

const payBasisLabel = computed(() => {
  if (props.payBasis) return props.payBasis;
  const code = props.identity.payBasis;
  const opt = props.payBasisOptions.find((o) => o.code === code);
  return opt?.name || code || "—";
});

const workModeLabel = computed(() => {
  if (props.workMode) return props.workMode;
  const code = props.identity.workMode;
  const opt = props.workModeOptions.find((o) => o.code === code);
  return opt?.name || code || "—";
});
</script>

<style scoped>
.pay-range-identity-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}
.pay-range-identity-summary__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.pay-range-identity-summary__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}
.pay-range-identity-summary__hint {
  margin-left: auto;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
.pay-range-identity-summary__fields {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 0;
}
.pay-range-identity-summary__field {
  display: flex;
  flex: 1 1 160px;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pay-range-identity-summary__label {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}
.pay-range-identity-summary__value {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  overflow-wrap: anywhere;
}
</style>
